import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  alpha,
  useTheme,
  Fade,
  Slide,
  useMediaQuery,
} from '@mui/material';
import CalendarMonth from '@mui/icons-material/CalendarMonth';
import useAuth from '@modules/auth/hooks/api/useAuth';
import { safeLocalStorage } from '@common/utils/storage';

interface VideoStepProps {
  onComplete: () => void;
  isLoading: boolean;
}

const VideoStep = ({ onComplete, isLoading }: VideoStepProps) => {
  const theme = useTheme();
  const { user } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [_videoEnded, setVideoEnded] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const localWatched = safeLocalStorage.getItem('welcome_video_watched');
    if (localWatched === 'true') {
      setVideoEnded(true);
    }
    setTimeout(() => setIsInitializing(false), 100);
  }, []);

  const handleVideoEnded = useCallback(() => {
    setVideoEnded(true);
    safeLocalStorage.setItem('welcome_video_watched', 'true');
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    video.addEventListener('ended', handleVideoEnded);

    return () => {
      video.removeEventListener('ended', handleVideoEnded);
    };
  }, [handleVideoEnded]);

  const getButtonIcon = () => {
    if (isLoading || isInitializing) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress size={22} thickness={4} sx={{ color: 'white', opacity: 1 }} />
        </Box>
      );
    }

    return <CalendarMonth sx={{ fontSize: 22 }} />;
    // if (videoEnded) {
    //   return <CheckCircle sx={{ fontSize: 22 }} />;
    // }
    // return <Lock sx={{ fontSize: 22 }} />;
  };

  const getButtonBackground = () => {
    if (isLoading || isInitializing) {
      return theme.palette.primary.main;
    }
    // if (!videoEnded) {
    //   return alpha(theme.palette.grey[500], 0.2);
    // }
    return `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;
  };

  return (
    <>
      <Fade in timeout={600}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            variant="h4"
            fontWeight={700}
            gutterBottom
            color="primary"
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' }, mb: 0.5 }}
          >
            🎉 Félicitations {user?.firstname}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}
          >
            Visionnez cette vidéo pour découvrir comment obtenir vos premiers clients.
          </Typography>
        </Box>
      </Fade>

      <Slide direction="up" in timeout={500}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            // Use aspect-ratio instead of padding trick for better height control
            aspectRatio: {
              xs: '1 / 1',
              sm: '16 / 9',
            },
            // Limit max height to ensure button stays visible
            // Account for header (~80px), title (~60px), margins, and button (~80px)
            maxHeight: {
              xs: 'calc(100vh - 280px)',
              sm: 'calc(100vh - 240px)',
            },
            backgroundColor: alpha(theme.palette.common.black, 0.05),
            borderRadius: 2,
            overflow: 'hidden',
            mb: 2,
            boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}`,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <video
            ref={videoRef}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
            }}
            controls
            controlsList="nodownload"
            playsInline
            poster={
              isMobile
                ? '/images/thumbnails/ac-intro-thumbnail-square.png'
                : '/images/thumbnails/ac-intro-thumbnail.png'
            }
          >
            <source
              src={isMobile ? '/videos/ac-intro-square.mp4' : '/videos/ac-intro.mp4'}
              type="video/mp4"
            />
            <track kind="captions" />
          </video>
        </Box>
      </Slide>

      <Fade in timeout={800}>
        <Box sx={{ position: 'relative' }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={onComplete}
            // disabled={!videoEnded || isLoading || isInitializing}
            disabled={isLoading || isInitializing}
            startIcon={getButtonIcon()}
            sx={{
              height: 52,
              fontSize: '0.95rem',
              fontWeight: 600,
              borderRadius: 2,
              textAlign: 'left',
              textTransform: 'none',
              background: getButtonBackground(),
              backgroundColor: isLoading || isInitializing ? theme.palette.primary.main : undefined,
              // color:
              //   videoEnded || isLoading || isInitializing
              //     ? 'white'
              //     : alpha(theme.palette.text.primary, 0.4),
              color: 'white',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (() => {
                if (isLoading || isInitializing) {
                  return `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`;
                }
                // if (!videoEnded) {
                //   return 'none';
                // }
                return `0 3px 12px ${alpha(theme.palette.primary.main, 0.3)}`;
              })(),
              transition: 'all 0.3s ease',
              '&:hover': {
                background: (() => {
                  if (isLoading || isInitializing) {
                    return theme.palette.primary.dark;
                  }
                  // if (!videoEnded) {
                  //   return getButtonBackground();
                  // }
                  return `linear-gradient(90deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`;
                })(),
              },
              '&.Mui-disabled': {
                opacity: 1,
              },
            }}
          >
            {(() => {
              if (isLoading || isInitializing) {
                return 'Enregistrement...';
              }
              // if (!videoEnded) {
              //   return 'Regardez la vidéo en entier';
              // }
              return "Choisir mes heures d'ouverture";
            })()}
          </Button>
        </Box>
      </Fade>
    </>
  );
};

export default VideoStep;
